import { ButtonIcon, ButtonSize, ButtonVariant, IconVariants, Spinner, SpinnerSize } from "@next-platform/client-common-spicy-ui";
import React from "react";

import AvatarWithModal from "@/components/common/AvatarWithModal";
import { getAvatarUrl } from "@/utils";

interface ImageUploadPreviewProps {
  imageFileKey: string | null;
  isImageCleared: boolean;
  isUploading: boolean;
  onClearImage: () => void;
  isImageModalOpen: boolean;
  onImageModalOpenChange: (open: boolean) => void;
}

function ImageUploadPreview({
  imageFileKey,
  isImageCleared,
  isUploading,
  onClearImage,
  isImageModalOpen,
  onImageModalOpenChange,
}: ImageUploadPreviewProps): React.JSX.Element | null {
  // Only show preview when we have uploaded image, or during upload
  const hasContent = (imageFileKey && !isImageCleared) || isUploading;

  if (!hasContent) {
    return null;
  }

  return (
    <div className="flex items-center justify-start px-2 py-2  rounded-t-[13px]">
      <div className="flex items-center gap-3 flex-1">
        {/* Image thumbnail with click to expand using AvatarWithModal for enhanced modal experience */}
        <div className="flex-shrink-0 relative">
          <AvatarWithModal
            src={imageFileKey?.startsWith("data:") ? imageFileKey : (getAvatarUrl(imageFileKey || "", { returnWithOriginalRatio: true }) as string)}
            isOpen={isImageModalOpen}
            onOpenChange={onImageModalOpenChange}
            isClickable={!isUploading}
          >
            {imageFileKey && !isImageCleared ? (
              <img
                src={imageFileKey.startsWith("data:") ? imageFileKey : (getAvatarUrl(imageFileKey, { preset: "avatar64x64" }) as string)}
                alt=""
                className="w-20 h-20 object-cover rounded-[10px] border border-gray-4 hover:border-gray-6 transition-colors cursor-pointer"
                onClick={() => !isUploading && onImageModalOpenChange(true)}
              />
            ) : (
              /* Show placeholder during upload */
              <div className="w-20 h-20 bg-gray-3 rounded border border-gray-4 flex items-center justify-center cursor-pointer">
                <div className="w-8 h-8 bg-gray-6 rounded"></div>
              </div>
            )}
          </AvatarWithModal>
          {/* Upload progress overlay */}
          {isUploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-[10px] flex items-center justify-center">
              <Spinner size={SpinnerSize.SMALL} className="text-white" />
            </div>
          )}
          {/* Close button on preview */}
          <ButtonIcon
            icon={IconVariants.CLOSE}
            size={ButtonSize.XSMALL}
            variant={ButtonVariant.TRANSPARENT}
            iconColor={"black"}
            className="absolute -top-[5px] -right-[5px] bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] rounded-full"
            onClick={onClearImage}
            aria-label="Remove image"
          />
        </div>
      </div>
    </div>
  );
}

export default ImageUploadPreview;
